// src/pages/Casting/components/MessagesPanel.jsx

import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  FaBullhorn,
  FaCheckCircle,
  FaPlus,
  FaSave,
  FaTimesCircle,
  FaTrash,
} from "react-icons/fa";

const EMPTY_MESSAGE = {
  id: null,
  type: "INFO",
  title: "",
  content: "",
  target_mat_number: null,
  severity: "",
  is_pinned: false,
  is_active: true,
};

const stripHtml = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, "")
    .trim();

export default function MessagesPanel({
  eventId,
  mats = [],
  messages = [],
  setMessages,
  castState,
  setCastState,
  services,
}) {
  const {
    createEventCastMessage,
    updateEventCastMessage,
    toggleEventCastMessageActive,
    deleteEventCastMessage,
    upsertEventCastState,
  } = services;

  const [draft, setDraft] = useState(EMPTY_MESSAGE);
  const [saving, setSaving] = useState(false);
  const [diffusingId, setDiffusingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const sortedMats = useMemo(
    () =>
      [...(mats || [])].sort((a, b) => {
        const orderA =
          Number(a?.sort_order) ||
          Number(a?.mat_number) ||
          0;

        const orderB =
          Number(b?.sort_order) ||
          Number(b?.mat_number) ||
          0;

        return orderA - orderB;
      }),
    [mats]
  );

  const selected = useMemo(
    () =>
      (messages || []).find(
        (message) =>
          Number(message.id) === Number(draft.id)
      ) || null,
    [messages, draft.id]
  );

  const draftTargetExists = useMemo(() => {
    if (
      draft.target_mat_number === null ||
      draft.target_mat_number === undefined ||
      draft.target_mat_number === ""
    ) {
      return true;
    }

    return sortedMats.some(
      (mat) =>
        Number(mat.mat_number) ===
        Number(draft.target_mat_number)
    );
  }, [draft.target_mat_number, sortedMats]);

  const getMatLabel = (matNumber) => {
    if (
      matNumber === null ||
      matNumber === undefined ||
      matNumber === ""
    ) {
      return "Message global";
    }

    const mat = sortedMats.find(
      (item) =>
        Number(item.mat_number) ===
        Number(matNumber)
    );

    return mat?.label || `Tapis ${matNumber}`;
  };

  const startNew = () => {
    setDraft({
      ...EMPTY_MESSAGE,
    });
  };

  const selectMessage = (message) => {
    setDraft({
      id: message.id,
      type: message.type || "INFO",
      title: message.title || "",
      content: message.content || "",

      target_mat_number:
        message.target_mat_number === undefined ||
        message.target_mat_number === null
          ? null
          : Number(message.target_mat_number),

      severity: message.severity || "",
      is_pinned: Boolean(message.is_pinned),
      is_active: Boolean(message.is_active),
    });
  };

  const saveMessage = async () => {
    if (!eventId || saving) return;

    const content = String(
      draft.content || ""
    ).trim();

    if (!content) {
      toast.error(
        "Le contenu du message est obligatoire."
      );
      return;
    }

    if (!draftTargetExists) {
      toast.error(
        "La surface ciblée n’existe plus. Sélectionne une nouvelle cible."
      );
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...draft,

        title:
          String(draft.title || "").trim() ||
          null,

        content,

        target_mat_number:
          draft.target_mat_number === null ||
          draft.target_mat_number === undefined ||
          draft.target_mat_number === ""
            ? null
            : Number(draft.target_mat_number),

        severity:
          String(draft.severity || "").trim() ||
          null,

        is_pinned: Boolean(draft.is_pinned),
        is_active: Boolean(draft.is_active),
      };

      if (draft.id) {
        const updated =
          await updateEventCastMessage(
            eventId,
            payload
          );

        if (!updated) {
          throw new Error(
            "Le message mis à jour n’a pas été retourné."
          );
        }

        setMessages((currentMessages) =>
          (currentMessages || []).map(
            (message) =>
              Number(message.id) ===
              Number(updated.id)
                ? updated
                : message
          )
        );

        setDraft({
          id: updated.id,
          type: updated.type || "INFO",
          title: updated.title || "",
          content: updated.content || "",

          target_mat_number:
            updated.target_mat_number ===
              undefined ||
            updated.target_mat_number === null
              ? null
              : Number(
                  updated.target_mat_number
                ),

          severity: updated.severity || "",
          is_pinned: Boolean(
            updated.is_pinned
          ),
          is_active: Boolean(
            updated.is_active
          ),
        });

        toast.success("Message mis à jour.");
      } else {
        const created =
          await createEventCastMessage(
            eventId,
            payload
          );

        if (!created) {
          throw new Error(
            "Le message créé n’a pas été retourné."
          );
        }

        setMessages((currentMessages) => [
          created,
          ...(currentMessages || []),
        ]);

        setDraft({
          id: created.id,
          type: created.type || "INFO",
          title: created.title || "",
          content: created.content || "",

          target_mat_number:
            created.target_mat_number ===
              undefined ||
            created.target_mat_number === null
              ? null
              : Number(
                  created.target_mat_number
                ),

          severity: created.severity || "",
          is_pinned: Boolean(
            created.is_pinned
          ),
          is_active: Boolean(
            created.is_active
          ),
        });

        toast.success("Message créé.");
      }
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Erreur lors de l’enregistrement du message."
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (message) => {
    if (!eventId || !message?.id) return;

    setTogglingId(message.id);

    try {
      const result =
        await toggleEventCastMessageActive(
          eventId,
          message.id
        );

      if (!result) {
        throw new Error(
          "Aucune réponse retournée par le serveur."
        );
      }

      const nextIsActive = Boolean(
        result.is_active
      );

      setMessages((currentMessages) =>
        (currentMessages || []).map(
          (currentMessage) =>
            Number(currentMessage.id) ===
            Number(message.id)
              ? {
                  ...currentMessage,
                  is_active: nextIsActive,
                }
              : currentMessage
        )
      );

      if (
        Number(draft.id) ===
        Number(message.id)
      ) {
        setDraft((currentDraft) => ({
          ...currentDraft,
          is_active: nextIsActive,
        }));
      }

      toast.success(
        nextIsActive
          ? "Message activé."
          : "Message désactivé."
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Erreur lors du changement d’état du message."
      );
    } finally {
      setTogglingId(null);
    }
  };

  const removeMessage = async (message) => {
    if (!eventId || !message?.id) return;

    const confirmed = window.confirm(
      `Supprimer définitivement le message « ${
        message.title || "Sans titre"
      } » ?`
    );

    if (!confirmed) return;

    setDeletingId(message.id);

    try {
      const result =
        await deleteEventCastMessage(
          eventId,
          message.id
        );

      if (!result) {
        throw new Error(
          "Aucune réponse retournée par le serveur."
        );
      }

      setMessages((currentMessages) =>
        (currentMessages || []).filter(
          (currentMessage) =>
            Number(currentMessage.id) !==
            Number(message.id)
        )
      );

      if (
        Number(draft.id) ===
        Number(message.id)
      ) {
        setDraft({
          ...EMPTY_MESSAGE,
        });
      }

      /*
       * Si le message supprimé était diffusé,
       * on revient au mode tapis.
       */
      if (
        castState?.mode === "MESSAGE" &&
        Number(
          castState?.active_message_id
        ) === Number(message.id)
      ) {
        const updatedState =
          await upsertEventCastState(eventId, {
            mode: "MATS",
            active_message_id: null,
          });

        if (updatedState) {
          setCastState(updatedState);
        }
      }

      toast.success("Message supprimé.");
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Erreur lors de la suppression du message."
      );
    } finally {
      setDeletingId(null);
    }
  };

  const broadcastMessage = async (message) => {
    if (!eventId || !message?.id) return;

    if (!message.is_active) {
      toast.error(
        "Le message doit être actif avant d’être diffusé."
      );
      return;
    }

    setDiffusingId(message.id);

    try {
      /*
       * Un seul appel est nécessaire :
       * il définit à la fois le mode MESSAGE
       * et le message actif.
       */
      const updatedState =
        await upsertEventCastState(eventId, {
          mode: "MESSAGE",
          active_message_id: message.id,
        });

      if (!updatedState) {
        throw new Error(
          "L’état de diffusion n’a pas été retourné."
        );
      }

      setCastState(updatedState);

      toast.success(
        "Message diffusé sur l’écran."
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible de diffuser le message."
      );
    } finally {
      setDiffusingId(null);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      {/* Liste des messages */}
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-900 dark:shadow-[0_20px_60px_rgba(0,0,0,0.35)] lg:col-span-1">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 p-4 dark:border-white/10">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-sky-700 dark:text-sky-300">
              Diffusion
            </p>

            <h2 className="mt-1 text-lg font-black text-slate-950 dark:text-white">
              Messages
            </h2>
          </div>

          <button
            type="button"
            onClick={startNew}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 text-sm font-black text-white shadow-lg shadow-sky-600/20 transition hover:bg-sky-700"
          >
            <FaPlus />
            Nouveau
          </button>
        </div>

        <div className="max-h-[680px] space-y-2 overflow-y-auto p-3">
          {(messages || []).length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center dark:border-white/15">
              <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
                Aucun message
              </p>

              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Crée un message pour l’afficher sur
                l’écran du dojo.
              </p>
            </div>
          ) : (
            messages.map((message) => {
              const isSelected =
                Number(draft.id) ===
                Number(message.id);

              const isLive =
                castState?.mode === "MESSAGE" &&
                Number(
                  castState?.active_message_id
                ) === Number(message.id);

              return (
                <button
                  key={message.id}
                  type="button"
                  onClick={() =>
                    selectMessage(message)
                  }
                  className={[
                    "w-full rounded-2xl border p-3 text-left transition",
                    "focus:outline-none focus:ring-4 focus:ring-sky-500/15",

                    isSelected
                      ? "border-sky-600 bg-sky-600 text-white shadow-lg shadow-sky-600/20"
                      : "border-slate-200 bg-slate-50 text-slate-900 hover:border-sky-300 hover:bg-sky-50 dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-sky-500/10",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-black">
                        {message.title ||
                          "(Sans titre)"}
                      </p>

                      <p
                        className={[
                          "mt-1 line-clamp-3 whitespace-pre-line text-xs leading-5",
                          isSelected
                            ? "text-white/85"
                            : "text-slate-600 dark:text-slate-300",
                        ].join(" ")}
                      >
                        {stripHtml(
                          message.content || ""
                        )}
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-1.5">
                      {message.is_active ? (
                        <FaCheckCircle
                          className={
                            isSelected
                              ? "text-white"
                              : "text-emerald-500"
                          }
                          title="Message actif"
                        />
                      ) : (
                        <FaTimesCircle
                          className={
                            isSelected
                              ? "text-white/70"
                              : "text-slate-400"
                          }
                          title="Message inactif"
                        />
                      )}

                      {isLive ? (
                        <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-black uppercase text-white">
                          En direct
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div
                    className={[
                      "mt-3 flex flex-wrap items-center gap-2 text-[11px] font-bold",
                      isSelected
                        ? "text-white/80"
                        : "text-slate-500 dark:text-slate-400",
                    ].join(" ")}
                  >
                    <span>
                      {getMatLabel(
                        message.target_mat_number
                      )}
                    </span>

                    <span>•</span>

                    <span>
                      {message.type || "INFO"}
                    </span>

                    {message.is_pinned ? (
                      <>
                        <span>•</span>
                        <span>Épinglé</span>
                      </>
                    ) : null}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </section>

      {/* Éditeur */}
      <section className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-900 dark:shadow-[0_20px_60px_rgba(0,0,0,0.35)] sm:p-6 lg:col-span-2">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-sky-700 dark:text-sky-300">
              Éditeur
            </p>

            <h2 className="mt-1 text-xl font-black text-slate-950 dark:text-white">
              {draft.id
                ? "Modifier le message"
                : "Créer un message"}
            </h2>

            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Diffuse une information globale ou
              destinée à une surface précise.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {draft.id ? (
              <button
                type="button"
                onClick={() =>
                  selected &&
                  broadcastMessage(selected)
                }
                disabled={
                  !selected ||
                  !selected.is_active ||
                  diffusingId === selected.id
                }
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-black text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  selected?.is_active
                    ? "Diffuser ce message"
                    : "Active d’abord ce message"
                }
              >
                <FaBullhorn />

                {diffusingId === selected?.id
                  ? "Diffusion…"
                  : "Diffuser"}
              </button>
            ) : null}

            <button
              type="button"
              onClick={saveMessage}
              disabled={saving}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 text-sm font-black text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FaSave />

              {saving
                ? "Enregistrement…"
                : "Enregistrer"}
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div>
            <label
              htmlFor="message-type"
              className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-300"
            >
              Type
            </label>

            <select
              id="message-type"
              value={draft.type}
              onChange={(event) =>
                setDraft((currentDraft) => ({
                  ...currentDraft,
                  type: event.target.value,
                }))
              }
              className="mt-1 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-slate-950 dark:text-white"
            >
              <option value="INFO">
                Information
              </option>

              <option value="ALERT">
                Alerte
              </option>

              <option value="CALL">
                Appel
              </option>

              <option value="PODIUM">
                Podium
              </option>
            </select>
          </div>

          <div>
            <label
              htmlFor="message-target"
              className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-300"
            >
              Cible
            </label>

            <select
              id="message-target"
              value={
                draft.target_mat_number ?? ""
              }
              onChange={(event) =>
                setDraft((currentDraft) => ({
                  ...currentDraft,

                  target_mat_number:
                    event.target.value === ""
                      ? null
                      : Number(
                          event.target.value
                        ),
                }))
              }
              className={[
                "mt-1 h-11 w-full rounded-2xl border bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:ring-4 dark:bg-slate-950 dark:text-white",

                draftTargetExists
                  ? "border-slate-200 focus:border-sky-500 focus:ring-sky-500/10 dark:border-white/10"
                  : "border-amber-400 focus:border-amber-500 focus:ring-amber-500/10",
              ].join(" ")}
            >
              {!draftTargetExists ? (
                <option
                  value={draft.target_mat_number}
                  disabled
                >
                  Surface supprimée
                </option>
              ) : null}

              <option value="">
                Message global
              </option>

              {sortedMats.map((mat) => (
                <option
                  key={
                    mat.id || mat.mat_number
                  }
                  value={mat.mat_number}
                >
                  {mat.label ||
                    `Tapis ${mat.mat_number}`}
                </option>
              ))}
            </select>

            {!draftTargetExists ? (
              <p className="mt-2 text-xs font-bold leading-5 text-amber-600 dark:text-amber-300">
                La surface précédemment ciblée
                n’existe plus. Sélectionne une
                nouvelle cible ou choisis le message
                global.
              </p>
            ) : null}
          </div>

          <div className="md:col-span-2">
            <label
              htmlFor="message-title"
              className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-300"
            >
              Titre
            </label>

            <input
              id="message-title"
              value={draft.title}
              maxLength={120}
              onChange={(event) =>
                setDraft((currentDraft) => ({
                  ...currentDraft,
                  title: event.target.value,
                }))
              }
              className="mt-1 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-slate-950 dark:text-white"
              placeholder="Ex : Remise des médailles"
            />
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center justify-between gap-3">
              <label
                htmlFor="message-content"
                className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-300"
              >
                Contenu *
              </label>

              <span className="text-xs text-slate-400">
                {String(
                  draft.content || ""
                ).length}{" "}
                caractères
              </span>
            </div>

            <textarea
              id="message-content"
              value={draft.content}
              onChange={(event) =>
                setDraft((currentDraft) => ({
                  ...currentDraft,
                  content: event.target.value,
                }))
              }
              rows={6}
              className="mt-1 w-full resize-y rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold leading-6 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-slate-950 dark:text-white"
              placeholder="Ex : Tous les compétiteurs sont attendus sur l’aire de remise des médailles…"
            />
          </div>

          <div className="md:col-span-2">
            <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5 sm:flex-row sm:items-center">
              <label className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={Boolean(
                    draft.is_active
                  )}
                  onChange={(event) =>
                    setDraft(
                      (currentDraft) => ({
                        ...currentDraft,
                        is_active:
                          event.target.checked,
                      })
                    )
                  }
                  className="h-4 w-4 accent-sky-600"
                />

                Message actif
              </label>

              <label className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={Boolean(
                    draft.is_pinned
                  )}
                  onChange={(event) =>
                    setDraft(
                      (currentDraft) => ({
                        ...currentDraft,
                        is_pinned:
                          event.target.checked,
                      })
                    )
                  }
                  className="h-4 w-4 accent-sky-600"
                />

                Message épinglé
              </label>

              {draft.id && selected ? (
                <div className="flex flex-wrap gap-2 sm:ml-auto">
                  <button
                    type="button"
                    onClick={() =>
                      toggleActive(selected)
                    }
                    disabled={
                      togglingId === selected.id
                    }
                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-slate-200 px-3 text-xs font-black text-slate-800 transition hover:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
                  >
                    {selected.is_active ? (
                      <FaTimesCircle />
                    ) : (
                      <FaCheckCircle />
                    )}

                    {togglingId === selected.id
                      ? "Modification…"
                      : selected.is_active
                      ? "Désactiver"
                      : "Activer"}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      removeMessage(selected)
                    }
                    disabled={
                      deletingId === selected.id
                    }
                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-rose-600 px-3 text-xs font-black text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FaTrash />

                    {deletingId === selected.id
                      ? "Suppression…"
                      : "Supprimer"}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}